import {
  OrganizationAnalyticsService,
  OrganizationsService,
  type ProgramPublic,
  ProgramsService,
} from "@/client"
import { DashboardProgramFilter } from "@/components/Dashboard/DashboardProgramFilter"
import { PageShell } from "@/components/Layout"
import { useRouteBreadcrumbs } from "@/navigation/route-meta"
import {
  Badge,
  Box,
  Button,
  Flex,
  Grid,
  HStack,
  Input,
  Stack,
  Table,
  Text,
} from "@chakra-ui/react"
import { useQuery } from "@tanstack/react-query"
import { Fragment, useMemo, useState } from "react"
import { LuArrowDown, LuArrowUp, LuArrowUpDown } from "react-icons/lu"

import { SchoolAliasPanel } from "./SchoolAliasPanel"
import { SchoolAnalyticsCharts } from "./SchoolAnalyticsCharts"
import { SchoolQuizFilter } from "./SchoolQuizFilter"
import { buildSchoolRowsCsv, buildSchoolSubjectCsv } from "./schoolAnalyticsCsv"

interface SchoolAnalyticsProps {
  orgId: string
  embedded?: boolean
  programIds?: string[]
  onProgramIdsChange?: (programIds: string[]) => void
  programs?: ProgramPublic[]
  programsLoading?: boolean
  hideProgramFilter?: boolean
}

type SchoolSortKey =
  | "school"
  | "students"
  | "attempts"
  | "completed"
  | "completion"
  | "score"
  | "percentile"
  | "rank"
type SortDirection = "asc" | "desc"

const DEFAULT_SORT = {
  key: "rank" as SchoolSortKey,
  direction: "asc" as SortDirection,
}

function getDefaultSortDirection(key: SchoolSortKey): SortDirection {
  if (key === "school" || key === "rank") {
    return "asc"
  }

  return "desc"
}

function getAriaSort(
  active: boolean,
  direction: SortDirection,
): "ascending" | "descending" | "none" {
  if (!active) {
    return "none"
  }

  return direction === "asc" ? "ascending" : "descending"
}

function SortIndicator({
  active,
  direction,
}: {
  active: boolean
  direction: SortDirection
}) {
  if (!active) {
    return <LuArrowUpDown size={14} aria-hidden="true" />
  }

  return direction === "asc" ? (
    <LuArrowUp size={14} aria-hidden="true" />
  ) : (
    <LuArrowDown size={14} aria-hidden="true" />
  )
}

function SortableHeaderButton({
  label,
  active,
  direction,
  onClick,
}: {
  label: string
  active: boolean
  direction: SortDirection
  onClick: () => void
}) {
  return (
    <Button
      onClick={onClick}
      variant="ghost"
      size="xs"
      w="full"
      minW="unset"
      px={0}
      color="inherit"
      aria-label={label}
      justifyContent="flex-start"
    >
      <Flex align="center" gap={1}>
        <Text as="span" fontWeight="semibold">
          {label}
        </Text>
        <SortIndicator active={active} direction={direction} />
      </Flex>
    </Button>
  )
}

function triggerCsvDownload(filename: string, content: string) {
  const blob = new Blob([content], { type: "text/csv;charset=utf-8" })
  const url = URL.createObjectURL(blob)
  const link = document.createElement("a")
  link.href = url
  link.download = filename
  link.click()
  URL.revokeObjectURL(url)
}

export default function SchoolAnalytics({
  orgId,
  embedded = false,
  programIds,
  onProgramIdsChange,
  programs: controlledPrograms,
  programsLoading: controlledProgramsLoading,
  hideProgramFilter = false,
}: SchoolAnalyticsProps) {
  const [selectedQuizIds, setSelectedQuizIds] = useState<string[]>([])
  const [internalProgramIds, setInternalProgramIds] = useState<string[]>([])
  const [search, setSearch] = useState("")
  const [sortState, setSortState] = useState(DEFAULT_SORT)
  const [expandedSchoolKey, setExpandedSchoolKey] = useState<string | null>(
    null,
  )
  const [aliasPanelOpen, setAliasPanelOpen] = useState(false)
  const breadcrumbs = useRouteBreadcrumbs("organizations.school-analytics")
  const selectedProgramIds = programIds ?? internalProgramIds
  const normalizedProgramIds = useMemo(
    () => [...selectedProgramIds].sort(),
    [selectedProgramIds],
  )
  const handleProgramIdsChange = onProgramIdsChange ?? setInternalProgramIds

  const { data: organization } = useQuery({
    queryKey: ["organization", orgId],
    queryFn: () => OrganizationsService.getOrganization({ orgId }),
  })

  const { data: analytics, isLoading } = useQuery({
    queryKey: [
      "schoolAnalytics",
      orgId,
      "",
      "",
      selectedQuizIds,
      normalizedProgramIds,
    ],
    queryFn: () =>
      OrganizationAnalyticsService.readSchoolAnalytics({
        orgId,
        quizIds: selectedQuizIds.length ? selectedQuizIds : undefined,
        programIds: normalizedProgramIds.length
          ? normalizedProgramIds
          : undefined,
      }),
  })

  const { data: quizOptions } = useQuery({
    queryKey: ["schoolQuizOptions", orgId, "", ""],
    queryFn: () =>
      OrganizationAnalyticsService.readSchoolQuizOptions({ orgId }),
  })

  const { data: fetchedPrograms, isLoading: fetchedProgramsLoading } = useQuery(
    {
      queryKey: ["schoolAnalyticsPrograms", orgId],
      queryFn: () => ProgramsService.listPrograms({ limit: 200 }),
      enabled: programIds === undefined && controlledPrograms === undefined,
    },
  )
  const programs = controlledPrograms ?? fetchedPrograms?.data ?? []
  const programsLoading =
    controlledProgramsLoading ??
    (controlledPrograms === undefined && fetchedProgramsLoading)

  const schools = useMemo(() => {
    const filteredSchools = (analytics?.schools ?? []).filter((school) =>
      school.school_name.toLowerCase().includes(search.trim().toLowerCase()),
    )

    const directionMultiplier = sortState.direction === "asc" ? 1 : -1

    return filteredSchools
      .map((school, sourceIndex) => ({ school, sourceIndex }))
      .sort((left, right) => {
        const leftSchool = left.school
        const rightSchool = right.school

        let comparison = 0
        switch (sortState.key) {
          case "school":
            comparison = leftSchool.school_name.localeCompare(
              rightSchool.school_name,
            )
            break
          case "students":
            comparison =
              leftSchool.unique_students - rightSchool.unique_students
            break
          case "attempts":
            comparison =
              leftSchool.started_attempts - rightSchool.started_attempts
            break
          case "completed":
            comparison =
              leftSchool.completed_attempts - rightSchool.completed_attempts
            break
          case "completion":
            comparison =
              leftSchool.completion_rate - rightSchool.completion_rate
            break
          case "score":
            comparison =
              (leftSchool.avg_score_percentage ?? -1) -
              (rightSchool.avg_score_percentage ?? -1)
            break
          case "percentile":
            comparison =
              (leftSchool.avg_percentile ?? -1) -
              (rightSchool.avg_percentile ?? -1)
            break
          case "rank":
            comparison = (leftSchool.rank ?? 999) - (rightSchool.rank ?? 999)
            break
        }

        if (comparison !== 0) {
          return comparison * directionMultiplier
        }

        return left.sourceIndex - right.sourceIndex
      })
      .map(({ school }) => school)
  }, [analytics?.schools, search, sortState])

  const handleSort = (key: SchoolSortKey) => {
    setSortState((current) => {
      if (current.key === key) {
        return {
          key,
          direction: current.direction === "asc" ? "desc" : "asc",
        }
      }

      return {
        key,
        direction: getDefaultSortDirection(key),
      }
    })
  }

  const getSortState = (key: SchoolSortKey) => ({
    active: sortState.key === key,
    direction:
      sortState.key === key ? sortState.direction : DEFAULT_SORT.direction,
  })

  const actions = (
    <HStack wrap="wrap" gap={3}>
      <Input
        placeholder="Search schools"
        value={search}
        onChange={(event) => setSearch(event.target.value)}
        maxW="220px"
      />
      <SchoolQuizFilter
        groups={quizOptions?.groups ?? []}
        selectedQuizIds={selectedQuizIds}
        onChange={setSelectedQuizIds}
        isLoading={!quizOptions}
      />
      {!hideProgramFilter && (
        <DashboardProgramFilter
          programs={programs}
          selectedProgramIds={selectedProgramIds}
          onChange={handleProgramIdsChange}
          isLoading={programsLoading}
        />
      )}
      <Button variant="outline" onClick={() => setAliasPanelOpen(true)}>
        Manage aliases
      </Button>
      <Button
        variant="outline"
        onClick={() =>
          analytics
            ? triggerCsvDownload(
                "school-analytics.csv",
                buildSchoolRowsCsv(analytics),
              )
            : null
        }
      >
        Export schools
      </Button>
      <Button
        variant="outline"
        onClick={() =>
          analytics
            ? triggerCsvDownload(
                "school-subjects.csv",
                buildSchoolSubjectCsv(analytics),
              )
            : null
        }
      >
        Export subjects
      </Button>
    </HStack>
  )

  const content = (
    <Stack gap={6}>
      {embedded ? actions : null}
      <Grid templateColumns={{ base: "1fr 1fr", lg: "repeat(5, 1fr)" }} gap={3}>
        <Box borderWidth="1px" borderRadius="md" px={4} py={3}>
          <Text color="fg.muted" fontSize="sm">
            School buckets
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {analytics?.summary.total_school_buckets ?? 0}
          </Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" px={4} py={3}>
          <Text color="fg.muted" fontSize="sm">
            Eligible schools
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {analytics?.summary.eligible_school_buckets ?? 0}
          </Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" px={4} py={3}>
          <Text color="fg.muted" fontSize="sm">
            Unique students
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {analytics?.summary.unique_students ?? 0}
          </Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" px={4} py={3}>
          <Text color="fg.muted" fontSize="sm">
            Unknown students
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {analytics?.summary.students_with_unknown_school ?? 0}
          </Text>
        </Box>
        <Box borderWidth="1px" borderRadius="md" px={4} py={3}>
          <Text color="fg.muted" fontSize="sm">
            Started attempts
          </Text>
          <Text fontSize="2xl" fontWeight="bold">
            {analytics?.summary.started_attempts ?? 0}
          </Text>
        </Box>
      </Grid>

      <SchoolAnalyticsCharts rows={analytics?.schools ?? []} />

      <Table.Root size="sm">
        <Table.Header>
          <Table.Row>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("school").active,
                getSortState("school").direction,
              )}
            >
              <SortableHeaderButton
                label="School"
                active={getSortState("school").active}
                direction={getSortState("school").direction}
                onClick={() => handleSort("school")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("students").active,
                getSortState("students").direction,
              )}
            >
              <SortableHeaderButton
                label="Students"
                active={getSortState("students").active}
                direction={getSortState("students").direction}
                onClick={() => handleSort("students")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("attempts").active,
                getSortState("attempts").direction,
              )}
            >
              <SortableHeaderButton
                label="Started"
                active={getSortState("attempts").active}
                direction={getSortState("attempts").direction}
                onClick={() => handleSort("attempts")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("completed").active,
                getSortState("completed").direction,
              )}
            >
              <SortableHeaderButton
                label="Completed"
                active={getSortState("completed").active}
                direction={getSortState("completed").direction}
                onClick={() => handleSort("completed")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("completion").active,
                getSortState("completion").direction,
              )}
            >
              <SortableHeaderButton
                label="Completion"
                active={getSortState("completion").active}
                direction={getSortState("completion").direction}
                onClick={() => handleSort("completion")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("score").active,
                getSortState("score").direction,
              )}
            >
              <SortableHeaderButton
                label="Avg score"
                active={getSortState("score").active}
                direction={getSortState("score").direction}
                onClick={() => handleSort("score")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("percentile").active,
                getSortState("percentile").direction,
              )}
            >
              <SortableHeaderButton
                label="Avg percentile"
                active={getSortState("percentile").active}
                direction={getSortState("percentile").direction}
                onClick={() => handleSort("percentile")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader
              aria-sort={getAriaSort(
                getSortState("rank").active,
                getSortState("rank").direction,
              )}
            >
              <SortableHeaderButton
                label="Rank"
                active={getSortState("rank").active}
                direction={getSortState("rank").direction}
                onClick={() => handleSort("rank")}
              />
            </Table.ColumnHeader>
            <Table.ColumnHeader>Subjects</Table.ColumnHeader>
          </Table.Row>
        </Table.Header>
        <Table.Body>
          {isLoading ? (
            <Table.Row>
              <Table.Cell colSpan={9}>Loading school analytics...</Table.Cell>
            </Table.Row>
          ) : null}
          {!isLoading && schools.length === 0 ? (
            <Table.Row>
              <Table.Cell colSpan={9}>No school analytics found.</Table.Cell>
            </Table.Row>
          ) : null}
          {schools.map((school) => {
            const hidden =
              school.metrics_hidden_reason === "minimum_cohort_not_met"
            return (
              <Fragment key={school.school_key}>
                <Table.Row key={school.school_key}>
                  <Table.Cell>
                    <HStack>
                      <Text>{school.school_name}</Text>
                      {school.is_unknown ? <Badge>Unknown</Badge> : null}
                    </HStack>
                  </Table.Cell>
                  <Table.Cell>{school.unique_students}</Table.Cell>
                  <Table.Cell>{school.started_attempts}</Table.Cell>
                  <Table.Cell>{school.completed_attempts}</Table.Cell>
                  <Table.Cell>{school.completion_rate.toFixed(2)}%</Table.Cell>
                  <Table.Cell>
                    {school.avg_score_percentage?.toFixed(2) ??
                      "Needs 10 students"}
                  </Table.Cell>
                  <Table.Cell>
                    {school.avg_percentile?.toFixed(2) ?? "Needs 10 students"}
                  </Table.Cell>
                  <Table.Cell>{school.rank ?? "-"}</Table.Cell>
                  <Table.Cell>
                    <HStack>
                      {!hidden && school.strongest_subjects?.[0] ? (
                        <Badge>
                          {school.strongest_subjects[0].subject_name}
                        </Badge>
                      ) : null}
                      <Button
                        size="xs"
                        variant="outline"
                        aria-label={`Details ${school.school_name}`}
                        onClick={() =>
                          setExpandedSchoolKey((current) =>
                            current === school.school_key
                              ? null
                              : school.school_key,
                          )
                        }
                      >
                        Details
                      </Button>
                    </HStack>
                  </Table.Cell>
                </Table.Row>
                {expandedSchoolKey === school.school_key ? (
                  <Table.Row key={`${school.school_key}-details`}>
                    <Table.Cell colSpan={9}>
                      <Stack gap={2}>
                        {(school.subject_details ?? []).map((subject) => (
                          <Box
                            key={subject.subject_id}
                            borderWidth="1px"
                            borderRadius="md"
                            px={3}
                            py={2}
                          >
                            <HStack justify="space-between">
                              <Text>{subject.subject_name}</Text>
                              <Text>
                                {subject.correctness_rate.toFixed(2)}%
                              </Text>
                            </HStack>
                          </Box>
                        ))}
                      </Stack>
                    </Table.Cell>
                  </Table.Row>
                ) : null}
              </Fragment>
            )
          })}
        </Table.Body>
      </Table.Root>
    </Stack>
  )

  return (
    <>
      {embedded ? (
        content
      ) : (
        <PageShell
          title="School Analytics"
          description={organization?.name ?? "Organization school comparison"}
          breadcrumbs={breadcrumbs}
          actions={actions}
        >
          {content}
        </PageShell>
      )}
      <SchoolAliasPanel
        isOpen={aliasPanelOpen}
        onOpenChange={setAliasPanelOpen}
        orgId={orgId}
      />
    </>
  )
}
